import type React from 'react'
import { useEffect, useState } from 'react'
import { trackEvent } from '../../utils/events/trackEvent'
import '../../utils/privacy-compliance/oneTrustTestHelpers.utils' // Expose test helpers to window
import { OneTrustCategory } from '../../utils/privacy-compliance/oneTrustConsent.utils'

interface OneTrustConfig {
  oneTrust?: {
    allowCookieOptOut: boolean
    includeAutoBlockScript: boolean
    domainUuid: string
    reverse_banner_button_position?: string
    assetDomain?: string
  }
}

interface OneTrustCookieBannerProps {
  /** OneTrust configuration from store settings */
  config: OneTrustConfig | null
  /** Whether the banner should be enabled */
  enabled?: boolean
}

const validateAssetDomain = (assetDomain: string) =>
  /^[a-zA-Z0-9.-]+\.(cookielaw\.org|onetrust\.com)$/.test(assetDomain)

/**
 * OneTrust Cookie Banner Component
 *
 * Loads and initializes OneTrust cookie consent management based on configuration.
 * This component handles:
 * - Loading OneTrust scripts dynamically
 * - Initializing OneTrust with provided configuration
 * - Tracking banner interactions for analytics
 */
export const OneTrustCookieBanner: React.FunctionComponent<OneTrustCookieBannerProps> = ({
  config,
  enabled = true,
}) => {
  const [isInitialized, setIsInitialized] = useState(false)
  const oneTrustConfig = config?.oneTrust
  const domainUuid = oneTrustConfig?.domainUuid
  const includeAutoBlockScript = oneTrustConfig?.includeAutoBlockScript || false

  let assetDomain = 'cdn.cookielaw.org'
  if (validateAssetDomain(oneTrustConfig?.assetDomain)) {
    assetDomain = oneTrustConfig?.assetDomain
  }

  useEffect(() => {
    console.log('domainUuid', domainUuid)
    if (!domainUuid || !enabled || isInitialized) {
      return
    }

    try {
      // Add OneTrust auto-block script if enabled
      if (includeAutoBlockScript) {
        const existingAutoBlockScript = document.querySelector(
          `script[src*="/consent/${domainUuid}/OtAutoBlock.js"]`
        )

        if (!existingAutoBlockScript) {
          const autoBlockScript = document.createElement('script')
          autoBlockScript.src = `https://${assetDomain}/consent/${domainUuid}/OtAutoBlock.js`
          autoBlockScript.type = 'text/javascript'
          autoBlockScript.charset = 'UTF-8'
          document.head.appendChild(autoBlockScript)
        }
      }

      // Add OneTrust main script
      const existingMainScript = document.querySelector(
        `script[data-domain-script="${domainUuid}"]`
      )

      if (!existingMainScript) {
        const mainScript = document.createElement('script')
        mainScript.src = `https://${assetDomain}/scripttemplates/otSDKStub.js`
        mainScript.type = 'text/javascript'
        mainScript.charset = 'UTF-8'
        mainScript.setAttribute('data-domain-script', domainUuid)
        mainScript.setAttribute('data-document-language', 'true')

        mainScript.onload = () => {
          const existingWrapper = window.OptanonWrapper

          window.OptanonWrapper = function () {
            if (existingWrapper && typeof existingWrapper === 'function') {
              existingWrapper()
            }

            const activeGroups = window.OnetrustActiveGroups || ''
            console.log('[OneTrust Banner] OptanonWrapper called:', {
              activeGroups,
              timestamp: new Date().toISOString(),
            })

            // Dispatch custom event to notify listeners of consent change
            window.dispatchEvent(new CustomEvent('consent.onetrust'))

            trackEvent({
              id: 'privacy.onetrust.consent.changed',
              description: 'OneTrust consent preferences changed',
              data: {
                activeGroups: activeGroups,
                hasAnalyticsConsent: activeGroups.includes(`,${OneTrustCategory.PERFORMANCE},`),
              },
            })
          }

          trackEvent({
            id: 'privacy.onetrust.banner.initialized',
            description: 'OneTrust cookie banner initialized',
            data: {
              domainUuid: domainUuid,
              allowCookieOptOut: oneTrustConfig?.allowCookieOptOut,
              includeAutoBlockScript: includeAutoBlockScript,
            },
          })

          setIsInitialized(true)
        }

        mainScript.onerror = error => {
          console.error('Failed to load OneTrust script:', error)

          trackEvent({
            id: 'privacy.onetrust.banner.error',
            description: 'OneTrust banner script failed to load',
            data: {
              domainUuid: domainUuid,
              error: 'Script load failed',
            },
          })
        }

        document.head.appendChild(mainScript)
      }
    } catch (error) {
      console.error('Failed to initialize OneTrust banner:', error)

      trackEvent({
        id: 'privacy.onetrust.banner.initialization_error',
        description: 'OneTrust banner initialization failed',
        data: {
          error: error instanceof Error ? error.message : 'Unknown error',
          domainUuid: domainUuid,
        },
      })
    }
  }, [domainUuid, assetDomain, includeAutoBlockScript, oneTrustConfig, enabled, isInitialized])

  // This component doesn't render any visible UI - OneTrust handles the banner display
  return null
}

export default OneTrustCookieBanner
